import type { ReactNode } from 'react'
import type { SeoProps } from '../seo/Seo'
import { Seo } from '../seo/Seo'
import {
  findUtilityRoute,
  type UtilityRouteDefinition,
  type UtilityAvailability,
} from '../registry/utilityRegistry'
import { Button } from './button'
import { Card, CardContent } from './card'
import { ProcessingBadge } from './ProcessingBadge'
import { cn } from './utils'

export interface UtilityToolShellItem<
  Key extends string = string,
  Category extends string = string,
> {
  key: Key
  path: string
  category: Category
  title: string
  shortDescription: string
  longDescription: string
  icon: string
  status: 'available' | 'coming-soon'
  useCases: readonly string[]
  howTo: readonly string[]
  faq: readonly { question: string; answer: string }[]
  schema?: {
    applicationCategory?: string
  }
}

export interface UtilityToolShellSkin {
  categoryClassName?: string
  scenariosCardClassName?: string
  scenariosTitleClassName?: string
  scenarioChipClassName?: string
  relatedCardClassName?: string
  relatedAvailableClassName?: string
  relatedIconClassName?: string
}

export interface UtilityToolPageShellProps<Tool extends UtilityToolShellItem> {
  tool: Tool
  relatedTools?: readonly Tool[]
  seo: SeoProps
  domain: string
  ogImage?: string
  homeLabel: string
  scenariosLabel: string
  howToLabel: string
  faqLabel: string
  otherToolsLabel: string
  allToolsLabel: string
  categoryLabel: string
  categoryClassName: string
  processingBadge: ReactNode
  children: ReactNode
  width?: 'default' | 'wide'
  skin?: UtilityToolShellSkin
  allToolsHref?: string
  renderLink?: (props: { href: string; className?: string; children: ReactNode }) => ReactNode
}

export function UtilityToolPageShell<Tool extends UtilityToolShellItem>({
  tool,
  relatedTools = [],
  seo,
  ogImage,
  homeLabel,
  scenariosLabel,
  howToLabel,
  faqLabel,
  otherToolsLabel,
  allToolsLabel,
  categoryLabel,
  categoryClassName,
  processingBadge,
  children,
  width = 'default',
  skin,
  allToolsHref = '/#tools',
  renderLink = defaultRenderLink,
}: UtilityToolPageShellProps<Tool>) {
  const hasUseCases = tool.useCases.length > 0
  const hasRelatedTools = relatedTools.length > 0

  return (
    <>
      <Seo metadata={ogImage ? {
        ...seo.metadata,
        openGraph: { ...seo.metadata.openGraph, image: ogImage },
        twitter: { ...seo.metadata.twitter, image: ogImage, card: 'summary_large_image' },
      } : seo.metadata} />

      <div className={cn('mx-auto', width === 'wide' ? 'max-w-7xl' : 'max-w-6xl')}>
        <nav className="mb-5 hidden text-sm text-muted-foreground sm:block" aria-label={homeLabel}>
          {renderLink({
            href: '/',
            className: 'hover:text-foreground',
            children: homeLabel,
          })}
          <span className="mx-2">/</span>
          <span className="text-foreground">{tool.title}</span>
        </nav>

        <section
          className={cn(
            'grid gap-x-6 gap-y-4 sm:gap-y-8',
            hasUseCases && 'lg:grid-cols-[minmax(0,1fr)_280px] lg:items-end',
          )}
        >
          <div
            className="lg:col-start-1 lg:row-start-1"
            data-utility-shell-heading=""
          >
            <div className="mb-2 flex flex-wrap items-center gap-2 sm:mb-3">
              <span
                className={cn(
                  'hidden rounded-md border px-2.5 py-1 text-xs font-medium sm:inline-flex',
                  skin?.categoryClassName,
                  categoryClassName,
                )}
              >
                {categoryLabel}
              </span>
              {processingBadge}
            </div>
            <h1 className="max-w-3xl text-2xl font-bold tracking-normal sm:text-5xl">
              {tool.title}
            </h1>
            <p className="mt-2 line-clamp-2 max-w-3xl text-sm leading-6 text-muted-foreground sm:mt-4 sm:line-clamp-none sm:text-base sm:leading-7">
              {tool.longDescription}
            </p>
          </div>

          <div
            className={cn(hasUseCases && 'lg:col-span-2 lg:row-start-2')}
            data-utility-shell-primary=""
          >
            {children}
          </div>

          {hasUseCases && (
            <Card
              className={cn(
                'border-dashed bg-card/70 shadow-sm lg:col-start-2 lg:row-start-1 lg:self-end',
                skin?.scenariosCardClassName,
              )}
              data-utility-shell-scenarios=""
            >
              <CardContent className="p-4">
                <div
                  className={cn(
                    'text-xs font-medium uppercase text-muted-foreground',
                    skin?.scenariosTitleClassName,
                  )}
                >
                  {scenariosLabel}
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  {tool.useCases.map((item) => (
                    <span
                      key={item}
                      className={cn(
                        'rounded-md border bg-background px-2 py-1 text-xs text-muted-foreground',
                        skin?.scenarioChipClassName,
                      )}
                    >
                      {item}
                    </span>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </section>

        {(tool.howTo.length > 0 || tool.faq.length > 0) && (
          <section className="mt-12 grid gap-6 border-t pt-8 lg:grid-cols-2">
            {tool.howTo.length > 0 && (
              <div>
                <h2 className="text-xl font-semibold tracking-normal">{howToLabel}</h2>
                <ol className="mt-4 space-y-3">
                  {tool.howTo.map((step, index) => (
                    <li key={step} className="flex gap-3 text-sm leading-6 text-muted-foreground">
                      <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-foreground text-xs font-semibold text-background">
                        {index + 1}
                      </span>
                      <span>{step}</span>
                    </li>
                  ))}
                </ol>
              </div>
            )}

            {tool.faq.length > 0 && (
              <div>
                <h2 className="text-xl font-semibold tracking-normal">{faqLabel}</h2>
                <div className="mt-4 space-y-4">
                  {tool.faq.map((item) => (
                    <div key={item.question}>
                      <h3 className="font-medium tracking-normal">{item.question}</h3>
                      <p className="mt-1 text-sm leading-6 text-muted-foreground">{item.answer}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </section>
        )}

        {hasRelatedTools && (
          <section className="mt-12 border-t pt-8">
            <div className="flex items-center justify-between gap-4">
              <h2 className="text-xl font-semibold tracking-normal">{otherToolsLabel}</h2>
              <Button asChild variant="ghost" size="sm">
                {renderLink({ href: allToolsHref, children: allToolsLabel })}
              </Button>
            </div>
            <div className="mt-4 grid gap-3 md:grid-cols-3">
              {relatedTools.map((item) => renderRelatedTool(item, skin, renderLink))}
            </div>
          </section>
        )}
      </div>
    </>
  )
}

export interface UtilityRoutePageShellProps<RouteKey extends string> {
  registry: readonly UtilityRouteDefinition<RouteKey>[]
  routeKey: RouteKey
  seo: SeoProps
  domain: string
  siteName?: string
  ogImage?: string
  homeLabel: string
  scenariosLabel: string
  howToLabel: string
  faqLabel: string
  otherToolsLabel: string
  allToolsLabel: string
  categoryLabel?: string
  categoryClassName?: string
  useCases?: readonly string[]
  icon?: string
  children: ReactNode
  width?: 'default' | 'wide'
  skin?: UtilityToolShellSkin
  allToolsHref?: string
  renderLink?: (props: { href: string; className?: string; children: ReactNode }) => ReactNode
}

export function UtilityRoutePageShell<RouteKey extends string>({
  registry,
  routeKey,
  seo,
  domain,
  ogImage,
  homeLabel,
  scenariosLabel,
  howToLabel,
  faqLabel,
  otherToolsLabel,
  allToolsLabel,
  categoryLabel,
  categoryClassName = 'border-muted bg-card text-muted-foreground',
  useCases = [],
  icon,
  children,
  width,
  skin,
  allToolsHref,
  renderLink,
}: UtilityRoutePageShellProps<RouteKey>) {
  const route = findUtilityRoute(registry, routeKey)
  const tool = routeToShellItem(route, useCases, icon)

  return (
    <UtilityToolPageShell
      tool={tool}
      relatedTools={relatedRouteShellItems(route, registry)}
      seo={seo}
      domain={domain}
      ogImage={ogImage}
      homeLabel={homeLabel}
      scenariosLabel={scenariosLabel}
      howToLabel={howToLabel}
      faqLabel={faqLabel}
      otherToolsLabel={otherToolsLabel}
      allToolsLabel={allToolsLabel}
      categoryLabel={categoryLabel ?? route.nav.category ?? route.nav.label}
      categoryClassName={categoryClassName}
      processingBadge={
        <ProcessingBadge
          variant={route.processing.mode}
          label={route.processing.label}
          title={route.processing.title}
        />
      }
      width={width}
      skin={skin}
      allToolsHref={allToolsHref}
      renderLink={renderLink}
    >
      {children}
    </UtilityToolPageShell>
  )
}

function routeToShellItem<RouteKey extends string>(
  route: UtilityRouteDefinition<RouteKey>,
  useCases: readonly string[],
  icon = route.nav.label,
): UtilityToolShellItem<RouteKey> {
  return {
    key: route.routeKey,
    path: route.path,
    category: route.nav.category ?? 'utility',
    title: route.title,
    shortDescription: route.schema?.softwareDescription ?? route.description,
    longDescription: route.description,
    icon,
    status: shellStatus(route.availability),
    useCases,
    howTo: route.schema?.howTo ?? [],
    faq: route.schema?.faq ?? [],
    schema: {
      applicationCategory: route.schema?.applicationCategory,
    },
  }
}

function relatedRouteShellItems<RouteKey extends string>(
  route: UtilityRouteDefinition<RouteKey>,
  registry: readonly UtilityRouteDefinition<RouteKey>[],
): UtilityToolShellItem<RouteKey>[] {
  return (route.relatedTools ?? [])
    .map((related) => {
      const matchedRoute = registry.find((item) => item.routeKey === related.routeKey)
      if (!matchedRoute) return null

      return routeToShellItem(
        {
          ...matchedRoute,
          path: related.href ?? matchedRoute.path,
          title: related.label,
          description: related.description ?? matchedRoute.description,
        },
        [],
      )
    })
    .filter((tool): tool is UtilityToolShellItem<RouteKey> => Boolean(tool))
}

function shellStatus(availability: UtilityAvailability): 'available' | 'coming-soon' {
  return availability === 'available' ? 'available' : 'coming-soon'
}

function renderRelatedTool<Tool extends UtilityToolShellItem>(
  tool: Tool,
  skin: UtilityToolShellSkin | undefined,
  renderLink: (props: { href: string; className?: string; children: ReactNode }) => ReactNode,
): ReactNode {
  const disabled = tool.status !== 'available'
  const className = cn(
    'rounded-lg border bg-card p-4 shadow-sm transition-colors',
    skin?.relatedCardClassName,
    disabled
      ? 'cursor-not-allowed opacity-60'
      : cn('hover:border-foreground/30 hover:bg-accent/60', skin?.relatedAvailableClassName),
  )
  const content = (
    <div className="flex items-center gap-3">
      <span
        className={cn(
          'flex h-9 w-9 items-center justify-center rounded-md border bg-background text-sm font-semibold',
          skin?.relatedIconClassName,
        )}
      >
        {tool.icon}
      </span>
      <div>
        <div className="font-medium tracking-normal">{tool.title}</div>
        <p className="mt-1 text-xs leading-5 text-muted-foreground">{tool.shortDescription}</p>
      </div>
    </div>
  )

  if (disabled) {
    return (
      <span key={tool.key} aria-disabled="true" className={className}>
        {content}
      </span>
    )
  }

  return renderLink({
    href: tool.path,
    className,
    children: content,
  })
}

function defaultRenderLink({
  href,
  className,
  children,
}: {
  href: string
  className?: string
  children: ReactNode
}): ReactNode {
  return (
    <a href={href} className={className}>
      {children}
    </a>
  )
}
